// hooks/useDarkMode.tsx
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { ConfigProvider, theme } from 'antd';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
    themeMode: ThemeMode;
    toggleTheme: () => void;
    isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
        if (typeof window !== 'undefined') {
            const savedTheme = localStorage.getItem('theme') as ThemeMode | null;
            if (savedTheme) return savedTheme;
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'light';
    });

    const toggleTheme = useCallback(() => {
        setThemeMode(prev => {
            const newTheme = prev === 'dark' ? 'light' : 'dark';
            localStorage.setItem('theme', newTheme);
            return newTheme;
        });
    }, []);

    // Apply theme to document for CSS variables
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', themeMode);
        document.body.className = themeMode;
    }, [themeMode]);

    const value = {
        themeMode,
        toggleTheme,
        isDark: themeMode === 'dark'
    };

    // Custom dark theme tokens for softer colors
    const darkThemeTokens = {
        colorBgContainer: '#1a1a1a',
        colorBgElevated: '#242424',
        colorBgLayout: '#141414',
        colorBgSpotlight: '#2a2a2a',
        colorBgMask: 'rgba(0, 0, 0, 0.45)',
        colorBorder: '#434343',
        colorBorderSecondary: '#303030',
        colorFill: '#262626',
        colorFillSecondary: '#1f1f1f',
        colorFillTertiary: '#262626',
        colorFillQuaternary: '#141414',
        colorText: '#ffffff',
        colorTextSecondary: '#a6a6a6',
        colorTextTertiary: '#8c8c8c',
        colorTextQuaternary: '#595959',
        colorPrimary: '#4096ff',
        colorPrimaryHover: '#1677ff',
        colorPrimaryActive: '#0958d9',
        colorSuccess: '#49aa19',
        colorWarning: '#d89614',
        colorError: '#d32029',
        colorInfo: '#177ddc',
        borderRadius: 6,
        boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
        boxShadowSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
    };

    // Light theme tokens
    const lightThemeTokens = {
        colorPrimary: '#1890ff',
        borderRadius: 6,
    };

    return (
        <ThemeContext.Provider value={value}>
            <ConfigProvider
                theme={{
                    algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
                    token: themeMode === 'dark' ? darkThemeTokens : lightThemeTokens,
                    components: themeMode === 'dark' ? {
                        Input: {
                            activeBg: '#2a2a2a',
                            addonBg: '#2a2a2a',
                            colorBgContainer: '#2a2a2a',
                            colorBorder: '#434343',
                            activeBorderColor: '#4096ff',
                            hoverBorderColor: '#4096ff',
                            activeShadow: 'none',
                            colorText: '#ffffff',
                            colorTextPlaceholder: '#8c8c8c',
                        },
                    } : undefined,
                }}
            >
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
